import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import MaintenancePage from '@/pages/MaintenancePage';

interface MaintenanceGuardProps {
  children: React.ReactNode;
}

export function MaintenanceGuard({ children }: MaintenanceGuardProps) {
  const { user, isLoading: authLoading } = useAuth();

  const { data: isMaintenanceMode, isLoading: settingsLoading } = useQuery({
    queryKey: ['maintenance-mode'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .single();

      if (error) {
        console.error('Error fetching maintenance mode:', error);
        return false; // Default to not maintenance if error
      }

      return data?.value === 'true';
    },
    staleTime: 1000 * 60, // Cache for 1 minute
  });

  // Show loading while checking
  if (authLoading || settingsLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // If maintenance mode is active and user is NOT logged in, show maintenance page
  if (isMaintenanceMode && !user) {
    return <MaintenancePage />;
  }

  // Otherwise, render children normally
  return <>{children}</>;
}
